import abi from '../abi';
import { ZERO_ADDRESS } from '../addresses';
import { writeContract, prepareWriteContract, type SendTransactionResult, fetchFeeData } from '@wagmi/core';
import type { MarketIdentifiers } from '$lib/types/market';
import { estimateGasLimits } from '../estimateGaslimit';
import {
	buildAddMarginCallData,
	buildRemoveMarginCallData,
	removeMarginFromOtherMarkets
} from '../builders/margin';
import type { UserMargin } from '$lib/types/user';
import { isItBedRockShutDownTime, throwError } from '$lib/home/util';
import { MAX_PRIORITY_FEE_PER_GAS } from '../contants';
import { getMaxFeeAndPriorityFee } from '../utils/gas';

export async function addMarginSmartContract(
	marginDelta: string,
	marketIdentifier: MarketIdentifiers,
	smartContractWalletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	const addMarginCallData = await buildAddMarginCallData(marginDelta, marketIdentifier);

	targetArray.push(...addMarginCallData._targetArray);
	callDataArray.push(...addMarginCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);


	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();

	const config = await prepareWriteContract({
		address: _smartContractWalletAddress,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		maxFeePerGas,
		maxPriorityFeePerGas,
		gas: gasLimit.toBigInt()
	});

	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function removeMarginSmartContract(
	marginDelta: string,
	marketIdentifier: MarketIdentifiers,
	smartContractWalletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];

	const functionName: string = 'cast';

	const removeMarginCallData = await buildRemoveMarginCallData(marginDelta, marketIdentifier);
	targetArray.push(...removeMarginCallData._targetArray);
	callDataArray.push(...removeMarginCallData._callDataArray);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);

	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		maxFeePerGas,
		maxPriorityFeePerGas,
		gas: gasLimit.toBigInt()
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}

export async function executeWithdrawAllUnusedMargins(
	marginsWithoutPositions: Array<UserMargin>,
	smartContractWalletAddress: `0x${string}`
) {
	if (isItBedRockShutDownTime()) {
		throwError();
	}
	const _smartContractWalletAddress = smartContractWalletAddress;
	const targetArray: Array<string> = [];
	const callDataArray: Array<string> = [];
	const functionName: string = 'cast';

	const marginCallData = removeMarginFromOtherMarkets(marginsWithoutPositions, '');

	targetArray.push(...marginCallData._targets);
	callDataArray.push(...marginCallData._callDatas);

	const gasLimit = await estimateGasLimits(
		_smartContractWalletAddress,
		targetArray,
		callDataArray,
		functionName
	);
	const { maxFeePerGas, maxPriorityFeePerGas } = await getMaxFeeAndPriorityFee();


	const config = await prepareWriteContract({
		address: _smartContractWalletAddress,
		abi: abi.scWalletMainImpl,
		functionName,
		args: [targetArray, callDataArray, ZERO_ADDRESS],
		gas: gasLimit.toBigInt(),
		maxFeePerGas,
		maxPriorityFeePerGas
	});
	const txData: SendTransactionResult = await writeContract(config);
	return txData;
}
